const { Core, Events, MediaQueries, onBlazeLoad } = Global;

const ACTIVE_CLASSNAME = '!active';
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const SCALE_EPSILON = 0.01;

customElements.define('product-gallery-expand', class extends Core {
    elements = {
        slider: 'slideshow-blaze'
    }

    render() {
        this._keydownHandler = this._handleKeydown.bind(this);
        onBlazeLoad(this._initSlider.bind(this));

        this.sub(Events.PRODUCT_GALLERY_SLIDE_CHANGE, this._slideToCurrent);
    
        this.$({
            'modal-open': this._handleModalOpen,
            'modal-close': this._handleModalClose
        })
    }

    _handleModalClose() {
        this.modalOpen = false;
        document.removeEventListener('keydown', this._keydownHandler);
        this.pub(Events.PRODUCT_GALLERY_EXPAND_CHANGE, this.modalOpen);
    }

    _handleModalOpen() {
        this.modalOpen = true;
        document.addEventListener('keydown', this._keydownHandler);
        this.pub(Events.PRODUCT_GALLERY_EXPAND_CHANGE, this.modalOpen);
    }

    _handleKeydown(event) {
        if(
            !this.modalOpen
            || MediaQueries.MOBILE.matches
            || event.defaultPrevented
            || event.altKey
            || event.ctrlKey
            || event.metaKey
            || event.shiftKey
        ) {
            return;
        }

        if(event.target.closest?.('input, textarea, select, [contenteditable="true"]')) {
            return;
        }

        const slider = this.$('slider')?.slider;
        if(!slider) {
            return;
        }

        if(event.key === 'ArrowLeft') {
            event.preventDefault();
            slider.prev();
        } else if(event.key === 'ArrowRight') {
            event.preventDefault();
            slider.next();
        }
    }

    async _initSlider() {
        await customElements.whenDefined(this.elements.slider);
        // TEMP: parent element should have it's own onSlide method
        this.$('slider').slider.onSlide(this._handleSlide.bind(this));
    }

    _handleSlide(index) {
        if(this.modalOpen) {
            this.pub(Events.PRODUCT_GALLERY_SLIDE_CHANGE, index);
        }
    }

    _slideToCurrent(imageIndex) {
        this.$('slider')?.slideTo(imageIndex);
    }

    destroy() {
        document.removeEventListener('keydown', this._keydownHandler);
    }
});

// decided to merge this into this file. This components could not be used separately
customElements.define('product-gallery-expand-zoom', class extends Core {
    elements = {
        image: 'img'
    }

    render() {
        this.active = false;
        this.scale = MIN_SCALE;
        this.translateX = 0;
        this.translateY = 0;
        this._mover = this._handleMouseMove.bind(this);
        this._touchStart = this._handleTouchStart.bind(this);
        this._touchMove = this._handleTouchMove.bind(this);
        this._touchEnd = this._handleTouchEnd.bind(this);

        this.addEventListener('touchstart', this._touchStart, { passive: false });
        this.addEventListener('touchmove', this._touchMove, { passive: false });
        this.addEventListener('touchend', this._touchEnd);
        this.addEventListener('touchcancel', this._touchEnd);

        if(!MediaQueries.MOBILE.matches) {
            this.$({
                click: this._handleClick
            });
        }

        // didn't find a better way
        // to handle out of view (slide change/modal close)
        // zoom disabling
        this.obs = new IntersectionObserver(([entry]) => {
            if(!entry.isIntersecting) {
                this._disableZoom();
            }
        })
    }

    _handleClick() {
        // Touch devices can have a desktop-sized viewport and emit a synthetic
        // click after a gesture. Do not let that toggle the mouse zoom mode.
        if(Date.now() - this._lastTouchTime < 500) {
            return;
        }

        this.active
            ? this._disableZoom()
            : this._enableZoom();
    }

    _disableZoom() {
        this.active = false;
        this.classList.remove(ACTIVE_CLASSNAME);
        this.removeEventListener('mousemove', this._mover);
        this.obs.unobserve(this);
        // position clean up
        this.style.removeProperty('--object-position');
        this._resetPinchZoom();
    }

    _enableZoom() {
        this._initSizes();
        this.active = true;
        this.classList.add(ACTIVE_CLASSNAME);
        this.addEventListener('mousemove', this._mover)
        this.obs.observe(this);
    }

    _initSizes() {
        // might not be available on render
        if(!this.width || !this.height) {
            this.width = this.offsetWidth;
            this.height = this.offsetHeight;
        }
    }

    _handleMouseMove(e) {
        let x = e.offsetX * 100 / this.width;
        let y = e.offsetY * 100 / this.height;
        this.style.setProperty('--object-position', `${x}% ${y}%`);
    }

    _handleTouchStart(e) {
        this._lastTouchTime = Date.now();

        if(e.touches.length >= 2) {
            e.preventDefault();
            e.stopPropagation();
            this._startPinch(e.touches);
            return;
        }

        if(e.touches.length === 1 && this.scale > MIN_SCALE + SCALE_EPSILON) {
            e.preventDefault();
            e.stopPropagation();
            this._startPan(e.touches[0]);
        }
    }

    _handleTouchMove(e) {
        if(e.touches.length >= 2) {
            e.preventDefault();
            e.stopPropagation();

            if(!this._pinching) {
                this._startPinch(e.touches);
            }

            const center = this._getTouchCenter(e.touches);
            const distance = this._getTouchDistance(e.touches);
            const scale = this._clampScale(
                this._gestureStartScale * distance / this._gestureStartDistance
            );
            const rect = this.getBoundingClientRect();
            const viewportCenterX = rect.left + rect.width / 2;
            const viewportCenterY = rect.top + rect.height / 2;
            const scaleChange = scale / this._gestureStartScale;
            const translateX = center.x - viewportCenterX - scaleChange * (
                this._gestureStartCenter.x - viewportCenterX - this._gestureStartX
            );
            const translateY = center.y - viewportCenterY - scaleChange * (
                this._gestureStartCenter.y - viewportCenterY - this._gestureStartY
            );

            this._setPinchTransform(scale, translateX, translateY);
            return;
        }

        if(e.touches.length === 1 && (this._panning || this.scale > MIN_SCALE + SCALE_EPSILON)) {
            e.preventDefault();
            e.stopPropagation();

            if(!this._panning) {
                this._startPan(e.touches[0]);
            }

            this._setPinchTransform(
                this.scale,
                this._panStartX + e.touches[0].clientX - this._panStartTouchX,
                this._panStartY + e.touches[0].clientY - this._panStartTouchY
            );
        }
    }

    _handleTouchEnd(e) {
        this._lastTouchTime = Date.now();

        if(e.touches.length >= 2) {
            this._startPinch(e.touches);
            return;
        }

        this._pinching = false;

        if(e.touches.length === 1 && this.scale > MIN_SCALE + SCALE_EPSILON) {
            this._startPan(e.touches[0]);
            return;
        }

        this._panning = false;

        if(this.scale <= MIN_SCALE + SCALE_EPSILON) {
            this._resetPinchZoom();
            this.obs.unobserve(this);
        }
    }

    _startPinch(touches) {
        this._pinching = true;
        this._panning = false;
        this._gestureStartDistance = Math.max(this._getTouchDistance(touches), 1);
        this._gestureStartCenter = this._getTouchCenter(touches);
        this._gestureStartScale = this.scale;
        this._gestureStartX = this.translateX;
        this._gestureStartY = this.translateY;
        this.obs.observe(this);
    }

    _startPan(touch) {
        this._panning = true;
        this._panStartTouchX = touch.clientX;
        this._panStartTouchY = touch.clientY;
        this._panStartX = this.translateX;
        this._panStartY = this.translateY;
    }

    _getTouchCenter(touches) {
        return {
            x: (touches[0].clientX + touches[1].clientX) / 2,
            y: (touches[0].clientY + touches[1].clientY) / 2
        };
    }

    _getTouchDistance(touches) {
        return Math.hypot(
            touches[0].clientX - touches[1].clientX,
            touches[0].clientY - touches[1].clientY
        );
    }

    _clampScale(scale) {
        return Math.min(Math.max(scale, MIN_SCALE), MAX_SCALE);
    }

    _setPinchTransform(scale, translateX, translateY) {
        const boundedTranslation = this._clampTranslation(translateX, translateY, scale);

        this.scale = scale;
        this.translateX = boundedTranslation.x;
        this.translateY = boundedTranslation.y;
        this.style.setProperty('--pinch-scale', this.scale);
        this.style.setProperty('--pinch-translate-x', `${this.translateX}px`);
        this.style.setProperty('--pinch-translate-y', `${this.translateY}px`);
    }

    _clampTranslation(x, y, scale) {
        const rect = this.getBoundingClientRect();
        const image = this.$('image');
        const imageAspectRatio = image?.naturalWidth && image?.naturalHeight
            ? image.naturalWidth / image.naturalHeight
            : rect.width / rect.height;
        const viewportAspectRatio = rect.width / rect.height;
        const renderedWidth = imageAspectRatio > viewportAspectRatio
            ? rect.width
            : rect.height * imageAspectRatio;
        const renderedHeight = imageAspectRatio > viewportAspectRatio
            ? rect.width / imageAspectRatio
            : rect.height;
        const maxX = Math.max(0, (renderedWidth * scale - rect.width) / 2);
        const maxY = Math.max(0, (renderedHeight * scale - rect.height) / 2);

        return {
            x: Math.min(Math.max(x, -maxX), maxX),
            y: Math.min(Math.max(y, -maxY), maxY)
        };
    }

    _resetPinchZoom() {
        this.scale = MIN_SCALE;
        this.translateX = 0;
        this.translateY = 0;
        this._pinching = false;
        this._panning = false;
        this.style.removeProperty('--pinch-scale');
        this.style.removeProperty('--pinch-translate-x');
        this.style.removeProperty('--pinch-translate-y');
    }

    destroy() {
        this.removeEventListener('touchstart', this._touchStart);
        this.removeEventListener('touchmove', this._touchMove);
        this.removeEventListener('touchend', this._touchEnd);
        this.removeEventListener('touchcancel', this._touchEnd);
        this.removeEventListener('mousemove', this._mover);
        this.obs?.disconnect();
    }
})
